import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@/components/Box';
import Arrival from '@/components/Arrival';
import cx from '@/utils/cx';
import useStyle from './style';

const Modal = ({
  visibility,
  onVisibilityChange,
  animation,
  height,
  width,
  overlay,
  position,
  children,
  className,
  containerClassName,
  zIndex,
  ...rest
}) => {
  const boxRef = useRef();
  const overlayRef = useRef();
  const classes = useStyle({
    height, width, animation, overlay, position, zIndex,
  });

  const handleContainerClick = (event) => {
    if (!boxRef.current.contains(event.target)) {
      onVisibilityChange(false);
    }
  };

  let previewsFocused = null;
  const onEnter = () => {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
    }
    previewsFocused = document.activeElement;
    overlayRef.current.setAttribute('aria-hidden', 'false');
    boxRef.current.setAttribute('tabindex', '0');
    boxRef.current.focus();
  };
  const onExit = () => {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'visible';
      if (previewsFocused) {
        previewsFocused.focus();
      }
    }
  };

  useEffect(() => () => {
    onExit();
  }, []);

  return (
    <Arrival in={visibility} unmountOnExit mountOnEnter onEnter={onEnter} onExited={onExit}>
      {(status) => (
        <div className={cx(classes.container, status, containerClassName)} aria-hidden="true" ref={overlayRef} onClick={handleContainerClick}>
          <Box
            _color="background"
            _border={width === 'fill' ? 'no' : 'md'}
            ref={boxRef}
            role="dialog"
            aria-modal="true"
            tabIndex="-1"
            className={cx(classes.modal, `${animation}-${status}`, className)}
            {...rest}>
            {children}
          </Box>
        </div>
      )}
    </Arrival>
  );
};

Modal.propTypes = {
  visibility: PropTypes.bool,
  onVisibilityChange: PropTypes.func,
  animation: PropTypes.oneOf(['fade', 'from-top', 'from-bottom', 'from-start', 'from-end']),
  height: PropTypes.oneOf(['fill', 'semi-fill', 'auto']),
  width: PropTypes.oneOf(['fill', 'semi-fill', 'auto']),
  position: PropTypes.oneOf(['top', 'start', 'end', 'bottom', 'center']),
  overlay: PropTypes.bool,
  zIndex: PropTypes.number,
  containerClassName: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

Modal.defaultProps = {
  visibility: false,
  onVisibilityChange: (() => { }),
  animation: 'fade',
  height: 'auto',
  width: 'auto',
  position: 'center',
  overlay: true,
  zIndex: 60,
  containerClassName: '',
  className: '',
};

export default Modal;
